import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export type SupportedLanguage = 'en' | 'hi';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'hi'];
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
const STORAGE_KEY = 'appLanguage';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  constructor(private translate: TranslateService) {}

  /**
   * Normalize stored language to supported value.
   * If user has unsupported language (e.g. 'bn', 'te'), treat as English.
   */
  normalizeLanguage(lang: string | undefined | null): SupportedLanguage {
    if (!lang || typeof lang !== 'string') {
      return DEFAULT_LANGUAGE;
    }
    const normalized = lang.toLowerCase().trim();
    return SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)
      ? (normalized as SupportedLanguage)
      : DEFAULT_LANGUAGE;
  }

  /**
   * Initialize language from user object (e.g. from localStorage or API).
   * Call on app load or after login.
   */
  initFromUser(user: { language?: string } | null | undefined): void {
    const lang = this.normalizeLanguage(user?.language);
    this.translate.use(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }

  /**
   * Set current UI language. Updates TranslateService and localStorage.
   * Does not persist to backend - that is done by Settings/Register pages.
   * Returns a Promise that resolves when the language is applied.
   */
  setLanguage(lang: SupportedLanguage): Promise<void> {
    localStorage.setItem(STORAGE_KEY, lang);
    return firstValueFrom(this.translate.use(lang)).then(() => {});
  }

  /**
   * Get current UI language.
   */
  getCurrentLanguage(): SupportedLanguage {
    const current = this.translate.currentLang as SupportedLanguage;
    return SUPPORTED_LANGUAGES.includes(current) ? current : DEFAULT_LANGUAGE;
  }

  /**
   * Initialize from localStorage when no user is present (e.g. guest on login/register pages).
   */
  initFromStorage(): void {
    const stored = localStorage.getItem(STORAGE_KEY);
    const lang = this.normalizeLanguage(stored);
    this.translate.use(lang);
  }
}
